import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { classNames } from "@via/utils/helpers.tsx";
import { type JSX, type ReactNode } from "react";

/**
 * One collapsible card in the run environment drawer.
 *
 * The body is ALWAYS mounted. The parent form is created with
 * shouldUnregister: true, so unmounting an input deletes its value from the
 * submitted payload. Collapsing therefore hides with a class and never with a
 * conditional render. Fields that are genuinely meant to leave the payload,
 * such as the Google Batch region, stay conditionally rendered inside the card
 * body, which is a different mechanism on purpose.
 */
export function CardShell({
  title,
  summary,
  open,
  onToggle,
  testId,
  children,
}: {
  title: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  testId: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section
      data-testid={`card-${testId}`}
      className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        data-testid={`card-toggle-${testId}`}
        className="flex w-full min-w-0 items-center gap-3 px-4 py-3 text-left">
        <span className="flex-shrink-0 text-sm font-semibold text-gray-900">
          {title}
        </span>
        <span
          data-testid={`card-summary-${testId}`}
          className={classNames(
            "min-w-0 flex-1 truncate text-right font-mono text-xs text-gray-500",
            open ? "invisible" : "visible"
          )}>
          {summary}
        </span>
        <ChevronDownIcon
          aria-hidden="true"
          className={classNames(
            "h-4 w-4 flex-shrink-0 text-gray-400 transition-transform",
            open ? "rotate-180" : ""
          )}
        />
      </button>
      <div
        data-testid={`card-body-${testId}`}
        className={classNames(
          "space-y-6 px-4 pb-5",
          open ? "block" : "hidden"
        )}>
        {children}
      </div>
    </section>
  );
}
